import { MoveDown, MoveUp, Search, X } from "lucide-react";
import { useCallback, useEffect } from "react";
import { IncidentQueryField } from "../types/salesforce";
import { useSalesforce } from "../hooks/useSalesforce";
import { useQueryBuilder } from "../hooks/useQueryBuilder";
import { Button } from "./UI/Button";

interface ReportParamsProps {
  className?: string;
}

export function ReportParams({ className }: Readonly<ReportParamsProps>) {
  const { getQueryFields, isLoading, error } = useSalesforce();
  const {
    selectedFields,
    setSelectedFields,
    availableFields,
    setAvailableFields,
  } = useQueryBuilder();

  useEffect(() => {
    const loadFields = async () => {
      try {
        const fields = await getQueryFields();
        setAvailableFields(fields);
      } catch (err) {
        console.error("Failed to load fields:", err);
      }
    };
    loadFields();
  }, [getQueryFields]);

  const handleDeselect = useCallback(
    (field: IncidentQueryField) => {
      // Create new arrays and pass them directly
      const newAvailableFields = [...availableFields, field];
      setAvailableFields(newAvailableFields);

      const updatedFields = selectedFields.filter(
        (gField) => gField.Field_API_Name__c !== field.Field_API_Name__c
      );
      setSelectedFields(updatedFields);
    },
    [selectedFields, availableFields]
  );

  const handleMoveUp = useCallback((index: number) => {
    const { selectedFields, setSelectedFields } = useQueryBuilder.getState();
    if (index === 0) return;

    const updated = [...selectedFields];
    [updated[index], updated[index - 1]] = [updated[index - 1], updated[index]];

    setSelectedFields(updated);
  }, []);

  const handleMoveDown = useCallback((index: number) => {
    const { selectedFields, setSelectedFields } = useQueryBuilder.getState();
    if (index === selectedFields.length - 1) return;

    const updated = [...selectedFields];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];

    setSelectedFields(updated);
  }, []);

  if (isLoading) {
    return (
      <div className={className}>
        <div
          className="p-6"
          style={{ border: "1px solid lightgrey", backgroundColor: "#FFF" }}
        >
          <div className="flex h-[300px] flex-col items-center justify-center rounded-md border p-4 text-gray-500 md:h-[400px] md:p-6">
            <Search className="mb-2 h-8 w-8 md:h-12 md:w-12" />
            <p className="text-sm md:text-base">Loading fields...</p>
          </div>
        </div>
      </div>
    );
  }

  if (selectedFields.length === 0) {
    return (
      <div className={className}>
        <div
          className="p-6"
          style={{ border: "1px solid lightgrey", backgroundColor: "#FFF" }}
        >
          <div className="flex h-[300px] flex-col items-center justify-center rounded-md border p-4 text-gray-500 md:h-[400px] md:p-6">
            <Search className="mb-2 h-8 w-8 md:h-12 md:w-12" />
            <p className="text-sm md:text-base">No fields selected</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        <div
          className="p-6 text-red-500"
          style={{ border: "1px solid lightgrey", backgroundColor: "#FFF" }}
        >
          <div className="flex h-[300px] flex-col items-center justify-center rounded-md border p-4 text-gray-500 md:h-[400px] md:p-6">
            <Search className="mb-2 h-8 w-8 md:h-12 md:w-12" />
            <p className="text-sm md:text-base">Error: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-[300px] overflow-y-auto border p-4 md:h-[400px] md:p-6"
      style={{ border: "1px solid lightgrey", backgroundColor: "#FFF" }}
    >
      <div className="space-y-4">
        {selectedFields.map((field, index) => {
          return (
            <div
              key={field.DeveloperName}
              className="group flex items-center justify-between gap-3 rounded-md p-2 pt-1 font-medium text-purple-900 hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-1">
                  <button
                    className="cursor-pointer border-0 p-1"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                  >
                    <MoveUp className="h-4 w-4" />
                  </button>
                  <button
                    className="cursor-pointer border-0 p-1"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === selectedFields.length - 1}
                  >
                    <MoveDown className="h-4 w-4" />
                  </button>
                </div>
                <span className="select-none">{field.Label}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeselect(field)}
                className="cursor-pointer border-0 text-red-500 hover:bg-red-100"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
